/**
 * The access token, written to BOTH channels that read it — the sibling of
 * `workspace-id-storage.ts`, and for exactly the same reason.
 *
 * There are two channels and they are not interchangeable:
 *  - `localStorage` — read by the browser API client (`lib/api/client.ts`),
 *    which attaches it as `Authorization: Bearer …`.
 *  - the `hf-app:access-token` COOKIE — the only one of the two a Next.js route
 *    handler or server component can read (`adminAuth()` in
 *    `lib/phishtrainer/backend.ts`, the LMS SCORM routes).
 *
 * WHAT WAS WRONG. Four places wrote the token and only three wrote the cookie:
 * `login()`, `setTokens()` and the auth-store's `onRehydrateStorage`. The
 * fourth — `tryRefreshToken()` in `lib/api/client.ts` — wrote localStorage
 * ONLY. The refresh runs on the 8h access-token expiry and the cookie carries a
 * 24h max-age, so in a browser tab that stays open (an operator's console does)
 * the client half was renewed forever and the cookie half decayed. At the 24h
 * mark `adminAuth()` began returning null mid-session and every server-side
 * PhishTrainer proxy fell through to its empty fallback — `'—'` and "not yet
 * tested" for a learner with real mastery — while the client half of the same
 * page kept answering correctly. No error, no login redirect (there is no
 * `middleware.ts` in apps/web to bounce a lapsed session), so the operator read
 * confident zeros. Rehydration re-armed it only on a full page load.
 *
 * Routing every write through here is what stops the two halves drifting apart
 * again. Safe to call on the server: both writes are no-ops without `window`.
 */
const KEY = 'hf-app:access-token';

/**
 * How long the browser cookies that mirror the session live.
 *
 * 24h, and deliberately the SAME number `persistWorkspaceId` uses. The two
 * cookies are a matched pair — `hf-app:access-token` is the credential
 * `adminAuth()` reads and `hf-app:workspace-id` is the tenant it reads it FOR —
 * and the failure mode is asymmetric: if the workspace cookie expires first,
 * adminAuth() silently falls back to the JWT's `defaultWorkspaceId`, a
 * no-orderBy first-membership lookup, and every server-rendered admin page
 * answers for the wrong tenant with no error and no UI signal. Raising this one
 * alone (e.g. to the refresh token's 7d) is exactly how that gets introduced.
 * If this ever moves, move workspace-id-storage.ts in the same commit.
 */
export const SESSION_COOKIE_MAX_AGE = 86400;

export function persistAccessToken(token: string): void {
  if (typeof window === 'undefined' || !token) return;
  try {
    window.localStorage.setItem(KEY, token);
  } catch {
    // Private mode / quota. The cookie below is the one the server needs.
  }
  document.cookie = `${KEY}=${token}; path=/; max-age=${SESSION_COOKIE_MAX_AGE}; SameSite=Lax`;
}

export function clearAccessToken(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  document.cookie = `${KEY}=; path=/; max-age=0`;
}
